/**
 * Preorders applied filters atoms
 *
 * This file contains Jotai atoms for managing the preorders applied filters state.
 */
import { atom } from 'jotai';

import type { AppliedFilterItem } from '@common/components/FiltersDrawer';

import { formatCommonDateFilterLabel } from '@common/lib/filters';
import { formatTimestampToDate } from '@common/lib/time';
import { CommonDateFilter } from '@common/types/filters';

import {
	estRetailPriceRangeAtom,
	modelFilterAtom,
	orderDateCustomRangeAtom,
	orderDateFilterAtom,
	preorderAmountRangeAtom,
	registrationStateFilterAtom,
	searchValueAtom,
	statusFilterAtom,
} from './preordersAtoms';

/**
 * Derived atom that combines all filter values to create a comprehensive list
 * of applied filters for the chips display
 */
export const allAppliedFiltersAtom = atom((get): AppliedFilterItem[] => {
	const appliedFilters: AppliedFilterItem[] = [];

	// Get all filter values
	const searchValue = get(searchValueAtom);
	const statuses = get(statusFilterAtom);
	const models = get(modelFilterAtom);
	const orderDateFilter = get(orderDateFilterAtom);
	const amountRange = get(preorderAmountRangeAtom);
	const estRetailPriceRange = get(estRetailPriceRangeAtom);
	const registrationState = get(registrationStateFilterAtom);

	// Add search query filter
	if (searchValue.trim()) {
		appliedFilters.push({
			id: 'search-query',
			title: 'Search',
			value: `"${searchValue.trim()}"`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add status filters - create individual chip for each selection
	if (statuses.length > 0) {
		statuses.forEach((status) => {
			appliedFilters.push({
				id: `status-${status}`,
				title: 'Status',
				value: status,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add model filters - create individual chip for each selection
	if (models.length > 0) {
		models.forEach((model) => {
			appliedFilters.push({
				id: `model-${model}`,
				title: 'Model',
				value: model,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add order date filter (single selection or custom range)
	if (orderDateFilter) {
		if (orderDateFilter === CommonDateFilter.CUSTOM) {
			const customRange = get(orderDateCustomRangeAtom);
			// Only show chip when from is set (matches API filtering behavior)
			if (customRange.from !== null) {
				const fromStr = formatTimestampToDate(customRange.from) ?? '...';
				const toStr = formatTimestampToDate(customRange.to) ?? '...';
				appliedFilters.push({
					id: 'order-date',
					title: 'Deposit date',
					value: `${fromStr} - ${toStr}`,
					clearFunction: () => {
						// This will be set by the component using the atom
					},
				});
			}
		} else {
			appliedFilters.push({
				id: 'order-date',
				title: 'Deposit date',
				value: formatCommonDateFilterLabel(orderDateFilter),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		}
	}

	// Add registration state filter
	if (registrationState) {
		appliedFilters.push({
			id: 'registration-state',
			title: 'State',
			value: registrationState,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add preorder amount range filter
	if (amountRange.min !== undefined || amountRange.max !== undefined) {
		const minStr = amountRange.min !== undefined ? `$${amountRange.min}` : '...';
		const maxStr = amountRange.max !== undefined ? `$${amountRange.max}` : '...';
		appliedFilters.push({
			id: 'preorder-amount',
			title: 'Deposit amount',
			value: `${minStr} - ${maxStr}`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add estimated retail price range filter
	if (estRetailPriceRange.min !== undefined || estRetailPriceRange.max !== undefined) {
		const minStr = estRetailPriceRange.min !== undefined ? `$${estRetailPriceRange.min}` : '...';
		const maxStr = estRetailPriceRange.max !== undefined ? `$${estRetailPriceRange.max}` : '...';
		appliedFilters.push({
			id: 'est-retail-price',
			title: 'Est. retail price',
			value: `${minStr} - ${maxStr}`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	return appliedFilters;
});

/**
 * Derived atom for the count of active filters
 */
export const activeFiltersCountAtom = atom((get) => {
	const allFilters = get(allAppliedFiltersAtom);
	return allFilters.length;
});
